const MIXPANEL_TOKEN = "4be97c4c5bfe31143928bd5188cce50f";
const MIXPANEL_API_HOST = "https://api-eu.mixpanel.com";
export const ANALYTICS_CONSENT_KEY = "rahewitt-analytics-consent";

let mixpanelClient = null;
let telemetryReady = false;
let telemetryDisabled = false;
let telemetryInitPromise = null;
let lastTrackedPath = null;
const pendingEvents = [];

export function getAnalyticsConsent() {
  if (typeof window === "undefined") {
    return null;
  }

  const value = window.localStorage.getItem(ANALYTICS_CONSENT_KEY);
  return value === "granted" ? true : value === "denied" ? false : null;
}

function shouldDisableTelemetry() {
  if (typeof window === "undefined") {
    return true;
  }

  if (window.navigator.doNotTrack === "1") {
    return true;
  }

  if (window.navigator.webdriver) {
    return true;
  }

  return /\bLighthouse\b/i.test(window.navigator.userAgent);
}

function flushPendingEvents() {
  if (!telemetryReady || pendingEvents.length === 0) {
    return;
  }

  const events = pendingEvents.splice(0, pendingEvents.length);

  for (const event of events) {
    try {
      mixpanelClient.track(event.name, event.properties);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn(`Mixpanel track failed for ${event.name}`, error);
      }
    }
  }
}

async function startTelemetry() {
  if (telemetryReady || telemetryDisabled) {
    return;
  }

  if (telemetryInitPromise) {
    return telemetryInitPromise;
  }

  telemetryInitPromise = import("mixpanel-browser")
    .then(({ default: mixpanel }) => {
      mixpanel.init(MIXPANEL_TOKEN, {
        api_host: MIXPANEL_API_HOST,
        autocapture: false,
        record_sessions_percent: 0,
        track_pageview: false,
        persistence: "localStorage",
        debug: import.meta.env.DEV,
      });

      mixpanel.opt_in_tracking();

      mixpanel.register({
        site_name: "rahewitt.com",
        site_area: "author-site",
      });

      mixpanelClient = mixpanel;
      telemetryReady = true;
      flushPendingEvents();
    })
    .catch((error) => {
      if (import.meta.env.DEV) {
        console.warn("Mixpanel init failed", error);
      }
    });

  return telemetryInitPromise;
}

export function initTelemetry() {
  if (telemetryReady || typeof window === "undefined") {
    return;
  }

  telemetryDisabled = shouldDisableTelemetry();

  if (telemetryDisabled) {
    return;
  }

  if (getAnalyticsConsent() === true) {
    startTelemetry();
  }
}

export function setAnalyticsConsent(granted) {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(ANALYTICS_CONSENT_KEY, granted ? "granted" : "denied");
  window.dispatchEvent(new CustomEvent("analytics-consent-changed", { detail: { granted } }));

  if (!granted) {
    pendingEvents.splice(0, pendingEvents.length);
    lastTrackedPath = null;

    if (mixpanelClient) {
      mixpanelClient.stop_session_recording?.();
      mixpanelClient.opt_out_tracking?.({ clear_persistence: true });
    }

    return;
  }

  telemetryDisabled = shouldDisableTelemetry();

  if (!telemetryDisabled) {
    mixpanelClient?.opt_in_tracking?.();
    startTelemetry();
  }
}

export function trackEvent(name, properties = {}) {
  if (telemetryDisabled || typeof window === "undefined" || getAnalyticsConsent() !== true) {
    return;
  }

  if (!telemetryReady || !mixpanelClient) {
    pendingEvents.push({ name, properties });
    return;
  }

  try {
    mixpanelClient.track(name, properties);
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn(`Mixpanel track failed for ${name}`, error);
    }
  }
}

export function trackPageView(pathname, search = "") {
  if (telemetryDisabled || typeof window === "undefined" || getAnalyticsConsent() !== true) {
    return;
  }

  const currentPath = `${pathname}${search}`;

  if (currentPath === lastTrackedPath) {
    return;
  }

  lastTrackedPath = currentPath;

  trackEvent("Page View", {
    page_url: window.location.href,
    page_title: document.title,
    path: pathname,
    search,
  });
}
